import { formatMoney } from "../../lib/format-fr.js";
import { useT } from "../../i18n/index.js";

export type EntryRow = {
  id: number;
  month: number;
  year: number;
  locked: boolean;
  fournituresConsommables: string;
  locationsChargesLocatives: string;
  primesAssurance: string;
  intermediairesHonoraires: string;
  deplacementsReception: string;
  fraisBancaires: string;
  impotsTaxes: string;
  chargesPersonnel: string;
  dotationsAmortissements: string;
  dotationsProvisions: string;
  autresChargesExploitation: string;
  impotSocietes: string;
  subventionsExploitation: string;
  autresProduitsExploitation: string;
  // F-recettes-encaissees (0072) — entradas do livre de caisse.
  recettesEncaisseesHt: string;
  tvaCollecteeEncaissements: string;
  recettesATrier: string;
  immobilisationsNettes: string | null;
  creancesClients: string | null;
  autresCreances: string | null;
  tresorerie: string | null;
  capitauxPropres: string | null;
  dettesFinancieres: string | null;
  dettesFournisseurs: string | null;
  autresDettes: string | null;
};

export type MonthCell = {
  month: number;
  year: number;
  entry: EntryRow | null;
};

/**
 * Calcula a lista de slots (month, year) entre startDate e endDate (inclusive).
 * Aceita FY com qualquer número de meses (12, 16, 6 etc.).
 */
export function buildMonthSlots(
  startDate: string,
  endDate: string,
): Array<{ month: number; year: number }> {
  const out: Array<{ month: number; year: number }> = [];
  const [sy, sm] = startDate.split("-").map(Number) as [number, number];
  const [ey, em] = endDate.split("-").map(Number) as [number, number];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push({ month: m, year: y });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

function sumCharges(e: EntryRow): number {
  return (
    Number(e.fournituresConsommables) +
    Number(e.locationsChargesLocatives) +
    Number(e.primesAssurance) +
    Number(e.intermediairesHonoraires) +
    Number(e.deplacementsReception) +
    Number(e.fraisBancaires)
  );
}
function sumOutrasCargas(e: EntryRow): number {
  return (
    Number(e.impotsTaxes) +
    Number(e.chargesPersonnel) +
    Number(e.dotationsAmortissements) +
    Number(e.dotationsProvisions) +
    Number(e.autresChargesExploitation) +
    Number(e.impotSocietes)
  );
}
function sumOutrosProdutos(e: EntryRow): number {
  return Number(e.subventionsExploitation) + Number(e.autresProduitsExploitation);
}
function hasBalance(e: EntryRow): boolean {
  return (
    e.immobilisationsNettes !== null ||
    e.creancesClients !== null ||
    e.autresCreances !== null ||
    e.tresorerie !== null ||
    e.capitauxPropres !== null ||
    e.dettesFinancieres !== null ||
    e.dettesFournisseurs !== null ||
    e.autresDettes !== null
  );
}

export function MonthlyEntryGrid({
  fyStartDate,
  fyEndDate,
  fyStatus,
  entries,
  isAdmin,
  canEdit,
  onEdit,
  onLock,
  onUnlock,
}: {
  fyStartDate: string;
  fyEndDate: string;
  fyStatus: "draft" | "open" | "closed";
  entries: ReadonlyArray<EntryRow>;
  isAdmin: boolean;
  canEdit: boolean;
  onEdit: (slot: MonthCell) => void;
  onLock: (entry: EntryRow) => void;
  onUnlock: (entry: EntryRow) => void;
}) {
  const t = useT();
  const MONTH_LABELS_PT = [
    t("monthlyEntryGrid.monthJanuary"),
    t("monthlyEntryGrid.monthFebruary"),
    t("monthlyEntryGrid.monthMarch"),
    t("monthlyEntryGrid.monthApril"),
    t("monthlyEntryGrid.monthMay"),
    t("monthlyEntryGrid.monthJune"),
    t("monthlyEntryGrid.monthJuly"),
    t("monthlyEntryGrid.monthAugust"),
    t("monthlyEntryGrid.monthSeptember"),
    t("monthlyEntryGrid.monthOctober"),
    t("monthlyEntryGrid.monthNovember"),
    t("monthlyEntryGrid.monthDecember"),
  ];
  const slots = buildMonthSlots(fyStartDate, fyEndDate);
  const byKey = new Map<string, EntryRow>();
  for (const e of entries) byKey.set(`${e.year}-${e.month}`, e);

  const fyClosed = fyStatus === "closed";

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-neutral-500">
          <tr className="border-b border-neutral-800">
            <th className="text-left px-3 py-2">{t("monthlyEntryGrid.colMonth")}</th>
            <th className="text-left px-3 py-2 w-20">{t("monthlyEntryGrid.colStatus")}</th>
            {/* F-recettes-encaissees — lado das entradas do livre de caisse. */}
            <th className="text-right px-3 py-2 font-mono">{t("monthlyEntryGrid.colRecettes")}</th>
            <th className="text-right px-3 py-2 font-mono">
              {t("monthlyEntryGrid.colChargesExternes")}
            </th>
            <th className="text-right px-3 py-2 font-mono">
              {t("monthlyEntryGrid.colOtherCharges")}
            </th>
            <th className="text-right px-3 py-2 font-mono">
              {t("monthlyEntryGrid.colOtherIncome")}
            </th>
            <th className="text-center px-3 py-2 w-24">{t("monthlyEntryGrid.colBilan")}</th>
            <th className="text-right px-3 py-2 w-44">{t("monthlyEntryGrid.colActions")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-900">
          {slots.map((s) => {
            const entry = byKey.get(`${s.year}-${s.month}`) ?? null;
            const monthLabel = MONTH_LABELS_PT[s.month - 1]!;
            return (
              <tr key={`${s.year}-${s.month}`}>
                <td className="px-3 py-2">
                  <span className="text-neutral-100">{monthLabel}</span>{" "}
                  <span className="text-neutral-500 text-xs">/ {s.year}</span>
                </td>
                <td className="px-3 py-2">
                  {entry === null ? (
                    <span className="text-xs text-neutral-600">
                      {t("monthlyEntryGrid.cellEmpty")}
                    </span>
                  ) : entry.locked ? (
                    <span className="inline-block rounded bg-amber-500/15 text-amber-300 px-2 py-0.5 text-xs">
                      {t("monthlyEntryGrid.statusLocked")}
                    </span>
                  ) : (
                    <span className="inline-block rounded bg-lime/15 text-lime px-2 py-0.5 text-xs">
                      {t("monthlyEntryGrid.statusOpen")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {entry === null ? (
                    <span className="text-neutral-300">—</span>
                  ) : (
                    <span
                      className={
                        Number(entry.recettesEncaisseesHt) > 0 ? "text-lime" : "text-neutral-300"
                      }
                    >
                      {formatMoney(Number(entry.recettesEncaisseesHt))}
                      {Number(entry.recettesATrier) > 0 && (
                        <span
                          className="ml-1 text-amber-400"
                          title={`${t("monthlyEntryGrid.aTrierTitlePre")} ${formatMoney(Number(entry.recettesATrier))}`}
                        >
                          ⚠
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                  {entry === null ? "—" : formatMoney(sumCharges(entry))}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                  {entry === null ? "—" : formatMoney(sumOutrasCargas(entry))}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                  {entry === null ? "—" : formatMoney(sumOutrosProdutos(entry))}
                </td>
                <td className="px-3 py-2 text-center">
                  {entry === null ? (
                    "—"
                  ) : hasBalance(entry) ? (
                    <span className="text-lime">✓</span>
                  ) : (
                    <span className="text-neutral-700">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    {canEdit && !fyClosed && (!entry || !entry.locked) && (
                      <button
                        onClick={() => onEdit({ ...s, entry })}
                        className="rounded-md border border-neutral-800 hover:border-lime hover:text-lime px-2 py-0.5 text-xs"
                      >
                        {entry ? t("monthlyEntryGrid.editBtn") : t("monthlyEntryGrid.createBtn")}
                      </button>
                    )}
                    {entry && !entry.locked && canEdit && (
                      <button
                        onClick={() => onLock(entry)}
                        className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-400 px-2 py-0.5 text-xs"
                      >
                        {t("monthlyEntryGrid.lockBtn")}
                      </button>
                    )}
                    {entry && entry.locked && isAdmin && !fyClosed && (
                      <button
                        onClick={() => onUnlock(entry)}
                        className="rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 px-2 py-0.5 text-xs"
                      >
                        {t("monthlyEntryGrid.unlockBtn")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="text-xs text-neutral-500 border-t border-neutral-800">
          <tr>
            <td className="px-3 py-2" colSpan={2}>
              {t("monthlyEntryGrid.totalLabel")}
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums text-lime">
              {formatMoney(entries.reduce((sum, e) => sum + Number(e.recettesEncaisseesHt), 0))}
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">
              {formatMoney(entries.reduce((sum, e) => sum + sumCharges(e), 0))}
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">
              {formatMoney(entries.reduce((sum, e) => sum + sumOutrasCargas(e), 0))}
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">
              {formatMoney(entries.reduce((sum, e) => sum + sumOutrosProdutos(e), 0))}
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
